import React, { useState, useEffect } from "react";
import BackdropComponent from "../../components/Spinner/Spinner";
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import TextLabel from "components/Common/TextLabel/TextLabel";
import { CustomButton } from "components/RosterConfig/RosterConfigStyle";
import { ContentCopy } from "@mui/icons-material";
import SnackBar from "components/SnackBar/SnackBar";
import {
  getInformaticaEmployeeData,
  getOracleidsPIIByFacilitiesTicketNumber,
} from "../../services/userServices";
import PropTypes from "prop-types";
import { fontStyles } from "components/Common/commonStyles";
import piiAuditLog from "../../utils/piiAuditLog";

const PiiReportFacilitiesDisplay = (props) => {
  const [loading, setLoading] = useState(false);
  const [piiData, setPiiData] = useState([]);

  const fieldsToFetch = ["national_identifier"];

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const copyToClipboard = () => {
    const piiTable = document.getElementById("piiTable");
    if (!piiTable) return;

    const headers = Array.from(
      piiTable.querySelectorAll("thead th, thead td"),
    ).map((cell) => cell.innerText.trim());

    const rows = Array.from(piiTable.querySelectorAll("tbody tr")).map((row) =>
      Array.from(row.querySelectorAll("td, th")).map((cell) =>
        cell.innerText.trim(),
      ),
    );

    let tableText = "";
    if (headers.length) {
      tableText += headers.join("\t") + "\n";
    }
    rows.forEach((row) => {
      tableText += row.join("\t") + "\n";
    });

    navigator.clipboard.writeText(tableText.trim());
    setSnack({
      open: true,
      message: "Copied!",
      severity: "success",
    });
  };

  const contentFontStyle = {
    ...fontStyles,
    fontSize: 14,
    letterSpacing: 0.1,
    padding: "16px",
  };

  const rowStyle = {
    height: "60px",
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const fetchInformaticaEmployeeData = async (oracleIds) => {
    const obj = {
      oracle_id: oracleIds,
      show_pii_field: fieldsToFetch.join(","),
    };

    const response = await getInformaticaEmployeeData(obj, false);

    if (response.status === true && response.data?.employee_datalist) {
      // Setting the employee datalist directly
      setPiiData(response.data.employee_datalist);

      // ASTT-7304: PII Access Logging. Only log when PII was actually shown.
      if (response.data.employee_datalist.length > 0) {
        piiAuditLog({
          oracleIds,
          userGroup: "FACBRZ_Group",
          sourceModule: "PIIReportByFacilities",
          moduleReference: props.facilitiesTicketNumber,
          piiFieldsShown: fieldsToFetch.join(","),
          piiFieldsMasked: "",
        });
      }
    } else {
      setSnack({
        open: true,
        message: "Failed to fetch  data from Informatica",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    async function fetchData() {
      setLoading(true);

      try {
        // Getting Oracle IDs from Facilities Ticket
        const resultData = await getOracleidsPIIByFacilitiesTicketNumber(
          props.facilitiesTicketNumber,
        );

        if (!resultData.status || resultData.data.length === 0) {
          setLoading(false);
          setSnack({
            open: true,
            message: "No records found for this Facilities Ticket",
            severity: "info",
          });
          return;
        }

        // Filterd out records with null OracleId
        const validOracleData = resultData.data.filter(
          (item) => item.OracleId !== null && item.OracleId !== undefined,
        );

        if (validOracleData.length === 0) {
          setLoading(false);
          setSnack({
            open: true,
            message: "No Oracle IDs found for this Facilities Ticket",
            severity: "info",
          });
          return;
        }

        // Collect all Oracle IDs (handling both OracleId and OracleIds fields)
        const oracleIdsList = validOracleData
          .map((item) => {
            if (item.OracleIds) {
              return item.OracleIds; // If OracleIds is a comma-separated string
            } else if (item.OracleId) {
              return String(item.OracleId);
            }
            return null;
          })
          .filter((id) => id !== null)
          .join(",");

        if (oracleIdsList) {
          await fetchInformaticaEmployeeData(oracleIdsList);
        } else {
          setSnack({
            open: true,
            message: "No valid Oracle IDs to fetch PII data",
            severity: "warning",
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnack({
          open: true,
          message: "Error fetching data",
          severity: "error",
        });
      }

      setLoading(false);
    }

    if (props.facilitiesTicketNumber) {
      fetchData();
    }
  }, [props.facilitiesTicketNumber]);

  return (
    <div>
      {loading ? <BackdropComponent /> : null}
      <Box sx={fontStyles}>
        <Paper>
          <Grid container>
            <Grid item xs={12}>
              <CustomButton
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: "#3299FF",
                  color: "#FFF",
                  borderRadius: 0,
                }}
                endIcon={<ContentCopy />}
                onClick={copyToClipboard}
              >
                Copy to Clipboard
              </CustomButton>
            </Grid>
            <Grid item xs={12}>
              <TableContainer
                sx={{ height: "calc(100vh - 35px)", overflowY: "auto" }}
              >
                <Table id="piiTable" size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={rowStyle}>
                      <TableCell>
                        <TextLabel text="Oracle ID" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="First Name" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Last Name" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="National Identifier" />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {piiData &&
                      piiData.length > 0 &&
                      piiData.map((employee, index) => {
                        const rowKey = `${employee.oracle_id}-${index}`;
                        return (
                          <TableRow key={rowKey} sx={rowStyle}>
                            <TableCell
                              sx={contentFontStyle}
                              component="th"
                              scope="row"
                              align="left"
                            >
                              {employee?.oracle_id || "N/A"}
                            </TableCell>
                            <TableCell sx={contentFontStyle} align="left">
                              {employee?.job_information?.first_name || "N/A"}
                            </TableCell>
                            <TableCell sx={contentFontStyle} align="left">
                              {employee?.job_information?.last_name || "N/A"}
                            </TableCell>
                            <TableCell sx={contentFontStyle} align="left">
                              {employee?.personal_information
                                ?.national_identifier || "N/A"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </div>
  );
};

PiiReportFacilitiesDisplay.propTypes = {
  facilitiesTicketNumber: PropTypes.string.isRequired,
};

export default PiiReportFacilitiesDisplay;
